import { useMemo, useRef, useState } from "react";
import InfiniteScroll from "react-infinite-scroll-component";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import { useRerunAnalysisJob } from "@/api/analysis/useAnalysisAPI";
import {
  useDeleteAnalysisHistory,
  useGetAnalysisHistoryDetail,
  useGetInfiniteAnalysisHistory
} from "@/api/analysisHistory/useAnalysisHistoryAPI";
import { useGetUsersAPI } from "@/api/user/useUserAPI";
import { DetailSection } from "@/components/analysisHistory/DetailSection";
import { EmptyState } from "@/components/analysisHistory/EmptyState";
import { FilterSection } from "@/components/analysisHistory/FilterSection";
import { HistoryCard } from "@/components/analysisHistory/HistoryCard";
import { LoadingShimmer } from "@/components/analysisHistory/LoadingShimmer";
import NotificationContent from "@/components/notification/notificationContent";
import LoadingWrapper from "@/components/ui/LoadingWrapper";
import {
  EMPTY_STATE_ANALYSIS_HISTORY,
  ERROR_MESSAGE
} from "@/constants/message";
import { useDataset } from "@/hooks/map/useDataset";
import useLoadingAndToast from "@/hooks/ui/useLoadingAndToast";
import { useDebounce } from "@/hooks/useDebounce";
import type { RootState } from "@/store";
import {
  resetDatasetState,
  setDatasetHistory
} from "@/store/slice/datasetSlice";
import {
  clearCurrRunningAnalysisId,
  resetSpatialState,
  setCurrRunningAnalysisId,
  setLatestAnalysisId
} from "@/store/slice/spatialSlice";
import {
  AnalysisJobItem,
  AnalysisJobStatus,
  TimeFilterValue
} from "@/types/analysis";

import dayjs from "dayjs";
import isBetween from "dayjs/plugin/isBetween";

import { mapAnalysisInputType, mapAnalysisResultType } from "./utils";

dayjs.extend(isBetween);

enum EmptyFlag {
  EMPTY_LIST = "EMPTY_LIST",
  SEARCH_NOT_FOUND = "SEARCH_NOT_FOUND",
  FILTER_NOT_FOUND = "FILTER_NOT_FOUND",
  HAVE_LIST_NOT_SELECTED = "HAVE_LIST_NOT_SELECTED"
}

export default function AnalysisHistoryPage() {
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const [selectedData, setSelectedData] = useState<string | null>(null);
  const [searchValue, setSearchValue] = useState("");
  const debouncedSearch = useDebounce(searchValue, 500);
  const { showSuccess, showError, showWarning, clearLoadingState } =
    useLoadingAndToast();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { handleClearDataset } = useDataset();

  const { datasets, rawDatasets, visualizationOrder } = useSelector(
    (state: RootState) => state.dataset
  );

  const [authorValue, setAuthorValue] = useState<{
    label: string;
    value: string;
  } | null>({
    label: "All",
    value: "all"
  });

  const [timeValue, setTimeValue] = useState<{
    label: string;
    value: string;
    range?: { startDate: string; endDate: string };
  } | null>({
    label: "Today",
    value: TimeFilterValue.Day
  });

  const params = useMemo(() => {
    const now = dayjs();
    return {
      ...(debouncedSearch?.length >= 2 && {
        query: debouncedSearch
      }),
      ...(authorValue?.value &&
        authorValue.value !== "all" && {
          author_id: authorValue.value
        }),
      ...(timeValue?.value === TimeFilterValue.Day && {
        created_at_from: now.startOf("day").toISOString(),
        created_at_to: now.endOf("day").toISOString()
      }),
      ...(timeValue?.value === TimeFilterValue.Last3Days && {
        created_at_from: now.subtract(3, "day").toISOString(),
        created_at_to: now.toISOString()
      }),
      ...(timeValue?.value === TimeFilterValue.Last7Days && {
        created_at_from: now.subtract(7, "day").toISOString(),
        created_at_to: now.toISOString()
      }),
      ...(timeValue?.value === TimeFilterValue.Last1Month && {
        created_at_from: now.subtract(1, "month").toISOString(),
        created_at_to: now.toISOString()
      }),
      ...(timeValue?.value === TimeFilterValue.Last3Months && {
        created_at_from: now.subtract(3, "month").toISOString(),
        created_at_to: now.toISOString()
      }),
      ...(timeValue?.value === TimeFilterValue.Custom &&
        timeValue.range && {
          created_at_from: dayjs(timeValue.range.startDate)
            .startOf("day")
            .toISOString(),
          created_at_to: dayjs(timeValue.range.endDate)
            .endOf("day")
            .toISOString()
        })
    };
  }, [debouncedSearch, authorValue, timeValue]);

  const { data: userData } = useGetUsersAPI({
    page: 1,
    per_page: 9999
  });

  const {
    data,
    isLoading,
    fetchNextPage,
    isFetchingNextPage,
    hasNextPage,
    refetch: refetchAnalysisHistory
  } = useGetInfiniteAnalysisHistory(params);

  const totalAnalysisHistory = useMemo(() => {
    return data?.pages?.[0]?.meta?.pagination?.total ?? 0;
  }, [data]);

  const rawHistoryData = useMemo(
    () => data?.pages.flatMap((page) => page.data) ?? [],
    [data]
  );

  const historyData: AnalysisJobItem[] = rawHistoryData.map((item) => ({
    id: item.id,
    name: item.analysis_name,
    status: AnalysisJobStatus.SUCCESS,
    created_by: item.author,
    created_at: item.created_at,
    expired_at: item.expired_at,
    datasets: item.datasets,
    input_type: mapAnalysisInputType(item.input) ?? "",
    output_type: mapAnalysisResultType(item.output) ?? "",
    estimated_time_secs: item.estimated_time_secs
  }));

  const selectedJob = useMemo(
    () => historyData.find((item) => item.id === selectedData),
    [historyData, selectedData]
  );

  const hasSearch = debouncedSearch?.length >= 2;
  const hasFilter =
    authorValue?.value !== "all" || timeValue?.value !== TimeFilterValue.Day;

  const listEmptyFlag = useMemo(() => {
    if (historyData.length === 0) {
      if (hasSearch) return EmptyFlag.SEARCH_NOT_FOUND;
      if (hasFilter) return EmptyFlag.FILTER_NOT_FOUND;
      return EmptyFlag.EMPTY_LIST;
    }
    return EmptyFlag.EMPTY_LIST;
  }, [historyData.length, hasSearch, hasFilter]);

  const detailEmptyFlag = useMemo(() => {
    if (!historyData.length) return EmptyFlag.EMPTY_LIST;
    if (!selectedJob) return EmptyFlag.HAVE_LIST_NOT_SELECTED;
    return EmptyFlag.EMPTY_LIST;
  }, [historyData.length, selectedJob]);

  const updateFilter =
    <T,>(setter: (v: T) => void) =>
    (value: T) => {
      setter(value);
      setSelectedData(null);
      scrollContainerRef.current?.scrollTo({ top: 0 });
    };

  const { data: selectedAnalysisHistory, isLoading: isLoadingDetail } =
    useGetAnalysisHistoryDetail(selectedData ?? "");

  const handleSelectJob = (id: string) => {
    if (id === selectedData) return;
    setSelectedData(id);
  };

  const authorOptions = useMemo(() => {
    const uniqueUsers = Array.from(
      new Map(
        userData?.data
          .filter((u) => u?.id && u?.fullname)
          .map((u) => [
            u.id,
            { label: u.fullname, value: u.id, description: u.email_address }
          ])
      ).values()
    );

    return [
      { label: "All", value: "all", description: "All authors" },
      ...uniqueUsers
    ];
  }, [userData]);

  const { mutate: deleteAnalysis } = useDeleteAnalysisHistory();

  const onDeleteAnalysis = (deletedId: string) => {
    deleteAnalysis(deletedId, {
      onSuccess: () => {
        setSelectedData(null);
        refetchAnalysisHistory();
        showSuccess(
          <NotificationContent
            title="Data deleted successfully."
            description="The analysis has been removed from the history."
          />
        );
      },
      onError: () => {
        showError(
          <NotificationContent
            title={ERROR_MESSAGE.SOMETHING_WENT_WRONG}
            description={ERROR_MESSAGE.SOMETHING_WENT_WRONG_DESCRIPTION}
          />
        );
      }
    });
  };

  const { mutate: reRun } = useRerunAnalysisJob({
    onError: () => {
      showError(
        <NotificationContent
          title={ERROR_MESSAGE.SOMETHING_WENT_WRONG}
          description={ERROR_MESSAGE.SOMETHING_WENT_WRONG_DESCRIPTION}
        />
      );
    },
    onSuccess: (data) => {
      clearLoadingState();
      if (data?.message) {
        showWarning(
          <NotificationContent
            title="Queue Limit Reached"
            description={data.message}
          />
        );
      } else {
        onOpenAnalysis(data?.id);
      }
    }
  });

  const onOpenAnalysis = (id: string) => {
    if (!id) return;
    // Clear previous analysis data
    handleClearDataset();
    dispatch(clearCurrRunningAnalysisId());
    dispatch(resetSpatialState());
    dispatch(resetDatasetState());
    // Set current analysis id
    dispatch(setCurrRunningAnalysisId(id));
    dispatch(setLatestAnalysisId(id));
    dispatch(
      setDatasetHistory({
        datasets: datasets,
        visualizationOrder: visualizationOrder,
        rawDatasets: rawDatasets
      })
    );
    // Navigate to spatial analysis page
    navigate("/");
  };

  const onRerunAnalysis = (id: string) => {
    if (!id) return;
    reRun({ analysisId: id });
  };

  return (
    <div className="flex flex-col h-full gap-4 p-6 overflow-hidden">
      <h1 className="text-2xl font-bold">Historical Analysis</h1>
      <div className="grid h-full gap-4 grid-cols-[340px_1fr] xl:grid-cols-[464px_1fr] overflow-hidden">
        <div className="flex flex-col h-full pt-2 overflow-hidden border-whisper">
          <FilterSection
            searchValue={searchValue}
            onSearchChange={updateFilter(setSearchValue)}
            authorValue={authorValue}
            onAuthorSelect={updateFilter(setAuthorValue)}
            timeValue={timeValue}
            onTimeSelect={updateFilter(setTimeValue)}
            disabled={isLoading}
            authorOptions={authorOptions}
          />
          <LoadingWrapper isLoading={isLoading}>
            <div className="mt-4 text-xs text-txtPlaceholder">
              All Historical ({totalAnalysisHistory})
            </div>
            <div
              id="scrollableDiv"
              ref={scrollContainerRef}
              className="flex flex-col flex-1 gap-2 pr-1 mt-4 overflow-y-auto"
              data-testid="analysis-history-scrollable"
            >
              {historyData.length > 0 ? (
                <InfiniteScroll
                  dataLength={historyData.length}
                  next={fetchNextPage}
                  hasMore={hasNextPage ?? false}
                  loader={null}
                  scrollableTarget="scrollableDiv"
                  scrollThreshold="95%"
                  className="flex flex-col gap-2"
                >
                  {historyData.map((item) => (
                    <div key={item.id}>
                      <HistoryCard
                        data={item}
                        isSelected={selectedData === item.id}
                        onSelect={() => handleSelectJob(item.id)}
                      />
                    </div>
                  ))}

                  {isFetchingNextPage && <LoadingShimmer />}
                </InfiniteScroll>
              ) : (
                <EmptyState
                  title={EMPTY_STATE_ANALYSIS_HISTORY.LIST[listEmptyFlag].title}
                  description={
                    EMPTY_STATE_ANALYSIS_HISTORY.LIST[listEmptyFlag].desc
                  }
                />
              )}
            </div>
          </LoadingWrapper>
        </div>
        <div className="flex flex-col h-full pt-2 overflow-hidden">
          <DetailSection
            key={selectedAnalysisHistory?.id}
            isLoading={isLoadingDetail}
            data={selectedAnalysisHistory}
            emptyMessage={EMPTY_STATE_ANALYSIS_HISTORY.DETAIL[detailEmptyFlag]}
            onDelete={onDeleteAnalysis}
            onOpen={onOpenAnalysis}
            onRerun={onRerunAnalysis}
          />
        </div>
      </div>
    </div>
  );
}
